<template>

  <div class="w-full mt-2">
    <a
      href="#"
      :class="isClickDisabled ? 'link-disabled cursor-not-allowed' : 'link-popup'"
      @click.prevent="isClickDisabled ? null : $emit('click')"
    >
      {{ linkText }}
    </a>
  </div>

</template>

<script>
import authService from '@/api/modules/auth.service';

/**
 * Link Forgot Password with Global Rate Limiting
 *
 * @module views/modules/components/ForgotPasswordLink
 * @author Jesus Niño <janmjesus@gmail.com>
 */
export default {
  name: 'ForgotPasswordLink',
  i18n: {
    messages: {
      en: {
        ForgotPasswordMsg: 'Forgot password?',
        ResendLinkMsg: 'Resend password instructions',
        ResendCooldownMsg: 'Resend in {seconds}s',
      },
    },
  },
  data() {
    return {
      countdownTimer: null,
      expiryTimer: null,
      currentTime: Date.now(),
      emailSentTime: null, // Store the email sent time directly in component data
      shortCooldownDuration: 30 * 1000, // 30 seconds
      tokenValidityDuration: 2 * 60 * 60 * 1000, // 2 hours
      localStorageKey: 'forgotPasswordLastSent',
    };
  },
  computed: {
    lastEmailSentTime() {
      // Use component data first, then fallback to localStorage
      if (this.emailSentTime) {
        return this.emailSentTime;
      }
      const stored = localStorage.getItem(this.localStorageKey);
      const timestamp = stored ? parseInt(stored, 10) : null;
      return timestamp;
    },
    timeSinceEmailSent() {
      if (!this.lastEmailSentTime) return 0;
      return this.currentTime - this.lastEmailSentTime;
    },
    isInShortCooldown() {
      return this.lastEmailSentTime && this.timeSinceEmailSent < this.shortCooldownDuration;
    },
    isInTokenValidityPeriod() {
      return this.lastEmailSentTime && this.timeSinceEmailSent < this.tokenValidityDuration;
    },
    shortCooldownTimeRemaining() {
      if (!this.isInShortCooldown) return 0;
      return Math.max(0, this.shortCooldownDuration - this.timeSinceEmailSent);
    },
    isClickDisabled() {
      return this.isInShortCooldown;
    },
    linkText() {
      if (this.isInShortCooldown) {
        const seconds = Math.ceil(this.shortCooldownTimeRemaining / 1000);
        return this.$t('ResendCooldownMsg', { seconds });
      }

      if (this.isInTokenValidityPeriod) {
        return this.$t('ResendLinkMsg');
      }

      return this.$t('ForgotPasswordMsg');
    },
  },
  async created() {
    // Check localStorage immediately when component is created
    this.currentTime = Date.now();

    // Initialize emailSentTime from localStorage if it exists
    const stored = localStorage.getItem(this.localStorageKey);
    if (stored) {
      this.emailSentTime = parseInt(stored, 10);
    }

    this.initializeTimers();

    const resp = await authService.getRecoverPasswordTokenDuration();

    this.tokenValidityDuration = resp.data.durationInSC * 1000;
  },
  mounted() {
    // Listen for password reset success to clear cooldown
    window.addEventListener('passwordResetSuccess', this.clearGlobalState);

    // Listen for email sent events to start cooldown
    window.addEventListener('forgotPasswordEmailSent', this.startGlobalCooldown);
  },
  beforeDestroy() {
    this.clearTimers();
    window.removeEventListener('passwordResetSuccess', this.clearGlobalState);
    window.removeEventListener('forgotPasswordEmailSent', this.startGlobalCooldown);
  },
  methods: {
    initializeTimers() {
      if (this.isInTokenValidityPeriod) {
        if (this.isInShortCooldown) {
          this.startCountdownTimer();
        }
        this.startExpiryTimer();
      }
    },
    startCountdownTimer() {
      this.clearCountdownTimer();

      // Update immediately
      this.currentTime = Date.now();

      this.countdownTimer = setInterval(() => {
        // Update current time to trigger reactivity
        this.currentTime = Date.now();

        if (!this.isInShortCooldown) {
          this.clearCountdownTimer();
        }
      }, 1000);
    },
    startExpiryTimer() {
      this.clearExpiryTimer();

      if (this.isInTokenValidityPeriod) {
        const timeUntilExpiry = this.tokenValidityDuration - this.timeSinceEmailSent;

        this.expiryTimer = setTimeout(() => {
          this.clearGlobalState();
        }, timeUntilExpiry);
      }
    },
    startGlobalCooldown(event) {
      // Use the timestamp from the event to ensure perfect synchronization
      const emailSentTime = event.detail?.timestamp || Date.now();

      // Set the timestamp directly in component data for Vue reactivity
      this.emailSentTime = emailSentTime;

      // Also set localStorage for persistence across page refreshes
      localStorage.setItem(this.localStorageKey, emailSentTime.toString());

      this.currentTime = Date.now();
      this.startCountdownTimer();
      this.startExpiryTimer();
      // Force Vue to recalculate computed properties
      this.$forceUpdate();
    },
    clearGlobalState() {
      localStorage.removeItem(this.localStorageKey);
      this.emailSentTime = null; // Clear the component data
      this.currentTime = Date.now();
      this.clearTimers();
      // Force Vue to recalculate computed properties
      this.$forceUpdate();
    },
    clearTimers() {
      this.clearCountdownTimer();
      this.clearExpiryTimer();
    },
    clearCountdownTimer() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    clearExpiryTimer() {
      if (this.expiryTimer) {
        clearTimeout(this.expiryTimer);
        this.expiryTimer = null;
      }
    },
  },
};
</script>
